import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SkeletonText } from "../shared/Skeleton";
import useDismissable from "../../hooks/useDismissable";
import {
  SCORE_COLORS,
  SCORE_TIER_LABELS,
  ACCOMMODATION_COLORS,
  scoreToTier,
  scoreTierColor,
  formatMonthShort,
  formatDayOfMonth,
  formatAttendance,
  formatCategory,
  dateSortKey,
  startOfTodayMs,
  ScoreBadge,
} from "./shared";
import ThinScroll from "../shared/ThinScroll";
import InfoTip from "../shared/InfoTip";
import { ErrorState } from "../shared/StateMessage";

// Tier badge styling. Keys match scoreToTier() output: campaign | quickwin | monitor | low.
// NOTE: backend returns an `effort_level` field per playbook, but its value drifts from
// the score-tier rule used elsewhere in the dashboard (e.g. score 78 returned as
// "quick_win"). To stay consistent with the Opportunity tab, we derive the tier from
// opportunity_score on the frontend and ignore effort_level. Remove this comment once
// the backend recomputes effort_level using the same thresholds as scoreToTier().
const TIER_BADGE_CLASSES = {
  campaign: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800",
  quickwin: "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800",
  monitor: "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800",
  low: "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700",
};

const MATCH_TYPE_CLASSES = {
  phrase: "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300",
  broad: "bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300",
  exact: "bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300",
};

const VOLUME_CLASSES = {
  low: "text-gray-500 dark:text-gray-400",
  medium: "text-amber-600 dark:text-amber-400",
  high: "text-emerald-600 dark:text-emerald-400",
};

// Detects bracketed placeholders Claude leaves for users to fill in:
// [X], [N], [date], [time], [link], [your name], [50-100], etc.
// Constrained to ≤30 chars and a single line so it doesn't match real bracketed prose.
const PLACEHOLDER_RX = /\[[A-Za-z0-9 _\-/$%.,]{1,30}\]/g;

const hasPlaceholders = (text) => {
  if (!text) return false;
  PLACEHOLDER_RX.lastIndex = 0;
  return PLACEHOLDER_RX.test(text);
};

const highlightPlaceholders = (text) => {
  if (!text) return text;
  const parts = [];
  let lastIndex = 0;
  let match;
  PLACEHOLDER_RX.lastIndex = 0;
  while ((match = PLACEHOLDER_RX.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(
      <span
        key={`ph-${match.index}`}
        title="Fill in before publishing"
        className="inline-flex items-baseline px-1 rounded bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-200 font-mono text-[0.95em] border border-amber-300/60 dark:border-amber-500/40 cursor-help"
      >
        {match[0]}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length > 0 ? parts : text;
};

const CopyButton = ({ text, label = "Copy", warnIfPlaceholders = false }) => {
  const [copied, setCopied] = useState(false);
  const onClick = async (event) => {
    event.stopPropagation();
    if (warnIfPlaceholders && hasPlaceholders(text)) {
      const proceed = window.confirm(
        "This text contains placeholders like [X] that need to be filled in before publishing. Copy anyway?"
      );
      if (!proceed) return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-colors ${
        copied
          ? "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300"
          : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
      }`}
    >
      {copied ? (
        <>
          <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          Copied
        </>
      ) : (
        <>
          <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
          {label}
        </>
      )}
    </button>
  );
};

const PLATFORM_ICONS = {
  facebook: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z" /></svg>
  ),
  instagram: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  ),
  twitter: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M22 5.8a8.49 8.49 0 0 1-2.36.64 4.13 4.13 0 0 0 1.81-2.27 8.21 8.21 0 0 1-2.61 1 4.1 4.1 0 0 0-7 3.74 11.64 11.64 0 0 1-8.45-4.29 4.16 4.16 0 0 0 1.27 5.49A4.09 4.09 0 0 1 2.8 9.7v.05a4.1 4.1 0 0 0 3.3 4 4.07 4.07 0 0 1-1.1.17 4.6 4.6 0 0 1-.77-.07 4.11 4.11 0 0 0 3.83 2.84A8.22 8.22 0 0 1 2 18.34a11.57 11.57 0 0 0 6.29 1.85A11.59 11.59 0 0 0 20 8.45v-.53a8.43 8.43 0 0 0 2-2.12z" /></svg>
  ),
  linkedin: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M20.47 2H3.53a1.45 1.45 0 0 0-1.47 1.43v17.14A1.45 1.45 0 0 0 3.53 22h16.94a1.45 1.45 0 0 0 1.47-1.43V3.43A1.45 1.45 0 0 0 20.47 2zM8.09 18.74h-3v-9h3zM6.59 8.48a1.56 1.56 0 1 1 0-3.12 1.57 1.57 0 1 1 0 3.12zm12.32 10.26h-3V13.9c0-1.33-.45-2.24-1.65-2.24a1.78 1.78 0 0 0-1.67 1.19 2.23 2.23 0 0 0-.11.79v5.1h-3v-9h3v1.28a3 3 0 0 1 2.67-1.47c1.95 0 3.42 1.27 3.42 4z" /></svg>
  ),
  tiktok: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.23a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.66z" /></svg>
  ),
};

const PlatformIcon = ({ platform }) => {
  const key = (platform || "").toLowerCase();
  return (
    <span className="inline-flex items-center gap-1 capitalize text-xs font-medium text-gray-700 dark:text-gray-200">
      <span className="text-[#32B1FF]">{PLATFORM_ICONS[key] ?? (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
        </svg>
      )}</span>
      {platform || "Social"}
    </span>
  );
};

const SectionHeader = ({ icon, title, count }) => (
  <div className="flex items-center gap-2 mb-3">
    <span className="text-[#32B1FF]">{icon}</span>
    <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400">
      {title}
    </span>
    {count != null && (
      <span className="text-[11px] font-bold text-gray-400 dark:text-gray-500">· {count}</span>
    )}
  </div>
);

const ICONS = {
  summary: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" /><line x1="9" y1="13" x2="15" y2="13" /><line x1="9" y1="17" x2="13" y2="17" />
    </svg>
  ),
  rate: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
    </svg>
  ),
  keywords: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  ads: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  social: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  ),
  email: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  landing: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" />
    </svg>
  ),
  partnership: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
};

const RateStrategySection = ({ rate }) => {
  if (!rate) return null;
  const pct = Number(rate.pct_increase || 0);
  const sites = Array.isArray(rate.applicable_site_types) ? rate.applicable_site_types : [];
  return (
    <div className="bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/40 rounded-lg p-4">
      <SectionHeader title="Rate Strategy" icon={ICONS.rate} />
      <div className="flex items-baseline gap-2 mb-2">
        <span className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
          {pct > 0 ? "+" : ""}{pct}%
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">nightly rate increase</span>
      </div>
      {sites.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {sites.map((site, index) => (
            <span key={index} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
              {site}
            </span>
          ))}
        </div>
      )}
      {rate.suggestion && (
        <div className="text-xs text-gray-700 dark:text-gray-200 mb-2 leading-relaxed">
          <span className="font-semibold text-gray-800 dark:text-gray-100">Suggestion: </span>
          {rate.suggestion}
        </div>
      )}
      {rate.rationale && (
        <div className="text-xs text-gray-600 dark:text-gray-400 italic leading-relaxed">
          {rate.rationale}
        </div>
      )}
    </div>
  );
};

const KeywordsSection = ({ keywords }) => {
  const list = Array.isArray(keywords) ? keywords : [];
  return (
    <div>
      <SectionHeader title="Suggested Keywords" count={list.length} icon={ICONS.keywords} />
      {list.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">No keywords suggested.</p>
      ) : (
        <div className="space-y-1.5">
          {list.map((keywordItem, index) => (
            <div
              key={index}
              className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 group"
            >
              <span className="flex-1 text-xs font-mono text-gray-800 dark:text-gray-100 truncate">
                {keywordItem.keyword}
              </span>
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${MATCH_TYPE_CLASSES[keywordItem.match_type] ?? "bg-gray-100 dark:bg-gray-800 text-gray-500"}`}>
                {keywordItem.match_type}
              </span>
              <span className={`text-[10px] uppercase font-semibold ${VOLUME_CLASSES[keywordItem.estimated_monthly_volume] ?? "text-gray-400"}`}>
                {keywordItem.estimated_monthly_volume || "—"}
              </span>
              <CopyButton text={keywordItem.keyword} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdDraftsSection = ({ ads }) => {
  const list = Array.isArray(ads) ? ads : [];
  return (
    <div>
      <SectionHeader title="Ad Drafts" count={list.length} icon={ICONS.ads} />
      {list.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">No ad drafts available.</p>
      ) : (
        <div className="space-y-2">
          {list.map((ad, index) => (
            <div
              key={index}
              className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-3"
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                  {ad.headline}
                </div>
                <CopyButton text={`${ad.headline}\n${ad.description}`} label="Copy ad" warnIfPlaceholders />
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                {highlightPlaceholders(ad.description)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SocialHooksSection = ({ hooks }) => {
  const list = Array.isArray(hooks) ? hooks : [];
  return (
    <div>
      <SectionHeader title="Social Hooks" count={list.length} icon={ICONS.social} />
      {list.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">No social posts suggested.</p>
      ) : (
        <div className="space-y-2">
          {list.map((hook, index) => (
            <div
              key={index}
              className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-3"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-3">
                  <PlatformIcon platform={hook.platform} />
                  {hook.timing && (
                    <span className="text-[11px] text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <path d="M16 2v4M8 2v4M3 10h18" />
                      </svg>
                      {hook.timing}
                    </span>
                  )}
                </div>
                <CopyButton text={hook.post_concept} label="Copy post" warnIfPlaceholders />
              </div>
              <div className="text-xs text-gray-700 dark:text-gray-200 leading-relaxed whitespace-pre-wrap">
                {highlightPlaceholders(hook.post_concept)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TextSection = ({ title, body, icon, accentClass = "bg-gray-50/60 dark:bg-gray-800/20" }) => {
  if (!body) return null;
  return (
    <div className={`${accentClass} border border-gray-200 dark:border-gray-800 rounded-lg p-4`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <SectionHeader title={title} icon={icon} />
        <CopyButton text={body} warnIfPlaceholders />
      </div>
      <div className="text-xs text-gray-700 dark:text-gray-200 leading-relaxed whitespace-pre-wrap">
        {highlightPlaceholders(body)}
      </div>
    </div>
  );
};

const ExecutiveSummarySection = ({ body }) => {
  if (!body) return null;
  return (
    <div>
      <SectionHeader title="Executive Summary" icon={ICONS.summary} />
      <blockquote className="pl-3 border-l-2 border-[#32B1FF]/40 text-sm text-gray-700 dark:text-gray-200 leading-relaxed italic">
        {body}
      </blockquote>
    </div>
  );
};

const PlaybookListItem = ({ pb, selected, onSelect }) => {
  const score = Number(pb.opportunity_score || 0);
  const tier = scoreToTier(score);
  const tierCls = TIER_BADGE_CLASSES[tier] ?? TIER_BADGE_CLASSES.low;
  const rate = pb.rate_adjustment || null;
  const ratePct = rate ? Number(rate.pct_increase || 0) : null;
  const keywords = Array.isArray(pb.suggested_keywords) ? pb.suggested_keywords : [];
  const ads = Array.isArray(pb.ad_copy_drafts) ? pb.ad_copy_drafts : [];
  const social = Array.isArray(pb.social_hooks) ? pb.social_hooks : [];
  const borderAccent = scoreTierColor(score);

  return (
    <button
      type="button"
      onClick={() => onSelect(pb.playbook_id)}
      className={`w-full text-left block px-3 py-3 border-l-4 transition-colors ${
        selected
          ? "bg-[#32B1FF]/5 dark:bg-[#32B1FF]/10"
          : "bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800/40"
      }`}
      style={{ borderLeftColor: selected ? "#32B1FF" : borderAccent }}
    >
      <div className="flex items-start gap-3">
        <ScoreBadge score={score} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className={`inline-flex items-center px-1.5 py-0 rounded-full text-[9px] font-bold uppercase tracking-wider border ${tierCls}`}>
              {SCORE_TIER_LABELS[tier]}
            </span>
            <span className="text-[10px] text-gray-500 dark:text-gray-400 font-semibold">
              {formatMonthShort(pb.start_date)} {formatDayOfMonth(pb.start_date)}
            </span>
          </div>
          <div className="text-xs font-semibold text-gray-900 dark:text-gray-100 leading-snug line-clamp-2 mb-1">
            {pb.event_name}
          </div>
          {pb.executive_summary && (
            <div className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed line-clamp-2 mb-2">
              {pb.executive_summary}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-gray-500 dark:text-gray-400">
            {ratePct != null && (
              <span className="text-emerald-600 dark:text-emerald-400 font-bold">
                {ratePct > 0 ? "+" : ""}{ratePct}%
              </span>
            )}
            {ratePct != null && <span>·</span>}
            <span>{keywords.length} keyword{keywords.length === 1 ? "" : "s"}</span>
            <span>·</span>
            <span>{ads.length} ad{ads.length === 1 ? "" : "s"}</span>
            <span>·</span>
            <span>{social.length} social</span>
          </div>
        </div>
      </div>
    </button>
  );
};

const SECTIONS = [
  { id: "summary", label: "Summary" },
  { id: "rate", label: "Rate" },
  { id: "keywords", label: "Keywords" },
  { id: "ads", label: "Ad Copy" },
  { id: "social", label: "Social" },
  { id: "email", label: "Email" },
  { id: "landing", label: "Landing" },
  { id: "partnership", label: "Partnership" },
];

export const PlaybookDetail = ({ pb }) => {
  const sectionRefs = useRef({});
  const scrollRef = useRef(null);

  // Reset scroll when switching playbooks
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [pb?.playbook_id]);

  if (!pb) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-8 text-sm text-gray-400 dark:text-gray-500">
        <svg className="w-10 h-10 mb-3 opacity-40" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
        Select a playbook on the left to view the full brief.
      </div>
    );
  }

  const rate = pb.rate_adjustment || null;
  const keywords = Array.isArray(pb.suggested_keywords) ? pb.suggested_keywords : [];
  const ads = Array.isArray(pb.ad_copy_drafts) ? pb.ad_copy_drafts : [];
  const social = Array.isArray(pb.social_hooks) ? pb.social_hooks : [];
  const accommodation = pb.best_accommodation;
  const accomCfg = ACCOMMODATION_COLORS[accommodation];
  const tier = scoreToTier(Number(pb.opportunity_score || 0));
  const tierCls = TIER_BADGE_CLASSES[tier] ?? TIER_BADGE_CLASSES.low;

  const available = {
    summary: !!pb.executive_summary,
    rate: !!rate,
    keywords: keywords.length > 0,
    ads: ads.length > 0,
    social: social.length > 0,
    email: !!pb.email_campaign_angle,
    landing: !!pb.landing_page_angle,
    partnership: !!pb.partnership_opportunity,
  };

  const scrollTo = (id) => {
    const el = sectionRefs.current[id];
    const container = scrollRef.current;
    if (!el || !container) return;
    const top = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
    container.scrollTo({ top, behavior: "smooth" });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="shrink-0 p-5 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-start gap-4">
          <ScoreBadge score={Number(pb.opportunity_score || 0)} size="lg" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${tierCls}`}>
                {SCORE_TIER_LABELS[tier]}
              </span>
              {accommodation && accomCfg && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${accomCfg.bg} ${accomCfg.text}`}>
                  Best for: {accommodation.toLowerCase() === "rv" ? "RV" : accommodation.charAt(0).toUpperCase() + accommodation.slice(1).toLowerCase()}
                </span>
              )}
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 leading-tight mb-1">
              {pb.event_name}
            </h3>
            <div className="text-xs text-gray-500 dark:text-gray-400 flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="font-semibold text-gray-600 dark:text-gray-300">
                {formatMonthShort(pb.start_date)} {formatDayOfMonth(pb.start_date)}
              </span>
              {pb.category && <span>· {formatCategory(pb.category)}</span>}
              {pb.distance_miles != null && <span>· {Number(pb.distance_miles).toFixed(1)} mi</span>}
              {pb.attendance_estimate ? <span>· {formatAttendance(pb.attendance_estimate)} attendees</span> : null}
              {pb.venue_name && <span>· {pb.venue_name}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Anchor nav */}
      <div className="shrink-0 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <ThinScroll className="flex gap-1 overflow-x-auto px-3 py-2 max-[499px]:overflow-x-visible max-[499px]:flex-wrap">
          {SECTIONS.filter((section) => available[section.id]).map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => scrollTo(section.id)}
              className="shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-[#32B1FF]/10 hover:text-[#32B1FF] transition-colors max-[499px]:shrink"
            >
              {section.label}
            </button>
          ))}
        </ThinScroll>
      </div>

      {/* Scrollable body */}
      <ThinScroll ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4 bg-gray-50/60 dark:bg-gray-950/30">
        {available.summary && (
          <section id="summary" ref={(el) => { sectionRefs.current.summary = el; }} className="scroll-mt-2">
            <ExecutiveSummarySection body={pb.executive_summary} />
          </section>
        )}
        {available.rate && (
          <section id="rate" ref={(el) => { sectionRefs.current.rate = el; }} className="scroll-mt-2">
            <RateStrategySection rate={rate} />
          </section>
        )}
        {available.keywords && (
          <section id="keywords" ref={(el) => { sectionRefs.current.keywords = el; }} className="scroll-mt-2">
            <KeywordsSection keywords={keywords} />
          </section>
        )}
        {available.ads && (
          <section id="ads" ref={(el) => { sectionRefs.current.ads = el; }} className="scroll-mt-2">
            <AdDraftsSection ads={ads} />
          </section>
        )}
        {available.social && (
          <section id="social" ref={(el) => { sectionRefs.current.social = el; }} className="scroll-mt-2">
            <SocialHooksSection hooks={social} />
          </section>
        )}
        {available.email && (
          <section id="email" ref={(el) => { sectionRefs.current.email = el; }} className="scroll-mt-2">
            <TextSection title="Email Campaign Angle" body={pb.email_campaign_angle} icon={ICONS.email} />
          </section>
        )}
        {available.landing && (
          <section id="landing" ref={(el) => { sectionRefs.current.landing = el; }} className="scroll-mt-2">
            <TextSection title="Landing Page Angle" body={pb.landing_page_angle} icon={ICONS.landing} />
          </section>
        )}
        {available.partnership && (
          <section id="partnership" ref={(el) => { sectionRefs.current.partnership = el; }} className="scroll-mt-2">
            <TextSection title="Partnership Opportunity" body={pb.partnership_opportunity} icon={ICONS.partnership} />
          </section>
        )}

        {pb.generated_at && (
          <div className="pt-2 text-[10px] text-gray-400 dark:text-gray-500">
            <span>Generated {new Date(pb.generated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
          </div>
        )}
      </ThinScroll>
    </div>
  );
};

const PlaybookGalleryPanel = ({ loading, error, playbooks }) => {
  const [tierFilter, setTierFilter] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [selectedId, setSelectedId] = useState(null);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);

  const closeMobileDetail = useCallback(() => setMobileDetailOpen(false), []);
  useDismissable(mobileDetailOpen, closeMobileDetail);

  const { filtered, pastCount, firstPastId } = useMemo(() => {
    const playbookArr = Array.isArray(playbooks) ? [...playbooks] : [];
    // Both sides must be local midnight. Parsing start_date with `new Date()`
    // gave midnight UTC, so west of UTC a playbook starting TODAY compared as
    // earlier than local midnight and got demoted into the "past" group.
    const todayTs = startOfTodayMs();
    const isPast = (playbook) => {
      if (!playbook.start_date) return false;
      const timestamp = dateSortKey(playbook.start_date, NaN);
      return Number.isFinite(timestamp) && timestamp < todayTs;
    };

    // Split first — past playbooks are a fixed bottom group, immune to
    // tier filter and sort toggle. Upcoming playbooks get both applied.
    const upcoming = [];
    const past = [];
    for (const playbook of playbookArr) (isPast(playbook) ? past : upcoming).push(playbook);

    const scopedUpcoming = tierFilter === "all"
      ? upcoming
      : upcoming.filter((playbook) => scoreToTier(Number(playbook.opportunity_score || 0)) === tierFilter);

    const FAR_FUTURE = Number.MAX_SAFE_INTEGER;
    const toTs = (playbook) => dateSortKey(playbook.start_date, FAR_FUTURE);

    if (sortBy === "date") {
      scopedUpcoming.sort((playbookA, playbookB) => toTs(playbookA) - toTs(playbookB));
    } else {
      scopedUpcoming.sort((playbookA, playbookB) => Number(playbookB.opportunity_score || 0) - Number(playbookA.opportunity_score || 0));
    }

    // Past group always sorts by most-recent-first regardless of toggle.
    past.sort((playbookA, playbookB) => toTs(playbookB) - toTs(playbookA));

    const combined = [...scopedUpcoming, ...past];
    const firstPast = past[0]?.playbook_id ?? null;
    return { filtered: combined, pastCount: past.length, firstPastId: firstPast };
  }, [playbooks, tierFilter, sortBy]);

  // Auto-select first playbook when filter or list changes
  useEffect(() => {
    if (filtered.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    if (!selectedId || !filtered.some((playbook) => playbook.playbook_id === selectedId)) {
      setSelectedId(filtered[0].playbook_id);
    }
  }, [filtered, selectedId]);

  const selectedPb = useMemo(
    () => filtered.find((playbook) => playbook.playbook_id === selectedId) || null,
    [filtered, selectedId]
  );

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800">
      <div className="p-5 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">Playbook Inbox</h3>
              <InfoTip text="Always all-time so the full backlog stays visible regardless of the page date range. Each playbook bundles channel suggestions, ad copy, and target keywords for one event." />
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                All-time
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Your prioritized marketing work queue. Sort by date to plan the week, or by score to prioritize impact.
            </p>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 shrink-0 text-right">
            {loading ? "Loading…" : (
              <>
                <div>{filtered.length} playbook{filtered.length === 1 ? "" : "s"}</div>
                {pastCount > 0 && (
                  <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                    {pastCount} past
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500 mr-1">
            Tier
          </span>
          {[
            { key: "all", label: "All" },
            { key: "campaign", label: "Campaign" },
            { key: "quickwin", label: "Quick Win" },
            { key: "monitor", label: "Monitor" },
            { key: "low", label: "Low Priority" },
          ].map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setTierFilter(opt.key)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                tierFilter === opt.key
                  ? "bg-[#32B1FF] text-white"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              }`}
            >
              {opt.label}
            </button>
          ))}

          <span className="h-4 w-px bg-gray-200 dark:bg-gray-700 mx-1"></span>

          <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500 mr-1">
            Sort
          </span>
          {[
            { key: "score", label: "Score" },
            { key: "date", label: "Date" },
          ].map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setSortBy(opt.key)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                sortBy === opt.key
                  ? "bg-[#32B1FF] text-white"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="p-5">
          <SkeletonText lines={4} lineClass="h-16 rounded-lg" gapClass="space-y-3" />
        </div>
      ) : error ? (
        <ErrorState message={error} className="m-5" />
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
          {(playbooks || []).length === 0
            ? "No playbooks generated yet."
            : "No playbooks match the current filter."}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] lg:h-[760px]">
          <ThinScroll className="border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-800 max-h-[480px] lg:max-h-none lg:h-full overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
            {filtered.map((pb) => (
              <React.Fragment key={pb.playbook_id}>
                {pb.playbook_id === firstPastId && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800/60 border-y-2 border-gray-300 dark:border-gray-700">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                      Past events
                    </span>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                      {pastCount} playbook{pastCount === 1 ? "" : "s"} · not affected by filters or sort
                    </span>
                  </div>
                )}
                <PlaybookListItem
                  pb={pb}
                  selected={pb.playbook_id === selectedId}
                  onSelect={(id) => {
                    setSelectedId(id);
                    setMobileDetailOpen(true);
                  }}
                />
              </React.Fragment>
            ))}
          </ThinScroll>
          <div className="hidden lg:block lg:h-full lg:overflow-hidden">
            <PlaybookDetail pb={selectedPb} />
          </div>
        </div>
      )}
      {mobileDetailOpen && selectedPb && (
        <div className="lg:hidden fixed inset-0 z-[120] flex flex-col bg-white dark:bg-gray-900">
          <div className="shrink-0 px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-xs uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400">
                Playbook
              </h2>
              <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                Marketing brief for the selected event.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setMobileDetailOpen(false)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors shrink-0"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex-1 min-h-0">
            <PlaybookDetail pb={selectedPb} />
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaybookGalleryPanel;
